import React from 'react';
import { Form, Row, Col, Button } from 'react-bootstrap';
import DatePicker from 'react-datepicker';

import { useWarehouseContext } from 'providers/WarehouseProvider';
import useFormValidation from 'hooks/useFormValidation';

import SearchableInput from 'components/common/SearchableInput';

import { vendorNameApi } from 'api';

import { CURRENCY_LIST } from 'constants/currency';
import {
  PO_TYPE,
  PO_SHIPPING_TYPE,
  PURCHASE_ORDER_VALIDATION_SCHEMA,
} from 'constants/purchaseOrder';

const PurchaseOrderForm = ({
  isCreate,
  isLoading,
  onSubmit,
  onCancel,
  purchaseOrder,
}) => {
  const { selectedWarehouse } = useWarehouseContext();

  const initialValues = {
    // Basic Details
    po_type: purchaseOrder?.po_type || '',
    expiration_date: purchaseOrder?.expiration_date || '',
    currency: purchaseOrder?.currency || '',
    // Vendor Details
    vendor: purchaseOrder?.vendor?.id || '',
    vendorName: purchaseOrder?.vendor?.name || '',
    // Logistic Charge
    shipping_type: purchaseOrder?.shipping_type || '',
    shipping_charge: purchaseOrder?.shipping_charge || '',
    shipping_charge_hsn_code: purchaseOrder?.shipping_charge_hsn_code || '',
    shipping_comments: purchaseOrder?.shipping_comments || '',
  };

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    validateForm,
    isValid,
  } = useFormValidation(initialValues, PURCHASE_ORDER_VALIDATION_SCHEMA);

  const handleVendorInputChange = value => {
    handleChange('vendor', '');
    handleChange('vendorName', value);
  };

  const handleVendorSelect = selectedVendor => {
    handleChange('vendor', selectedVendor.id);
    handleChange('vendorName', selectedVendor.name);
  };

  const handleExpirationDateChange = date => {
    if (!date) {
      handleChange('expiration_date', '');
      return;
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateString = `${year}-${month}-${day}`;

    const formattedDate = `${dateString}T05:30:00`;

    handleChange('expiration_date', formattedDate);
  };

  const handleSubmitForm = e => {
    e.preventDefault();

    if (!validateForm()) return;

    const filteredValues = Object.entries(values).reduce(
      (acc, [key, value]) => {
        if (value === '' || key === 'vendorName') {
          return acc;
        }

        if (!isCreate && key === 'vendor') {
          return acc;
        }

        acc[key] = value;
        return acc;
      },
      {},
    );

    if (isCreate) {
      filteredValues.warehouse = selectedWarehouse.id;
    }

    onSubmit(filteredValues);
  };

  return (
    <Form onSubmit={handleSubmitForm}>
      {/* Basic Details Section */}
      <div className="mb-4 border-bottom border-dotted">
        <h5 className="mb-3 fs-9">Basic Details</h5>

        <Row>
          <Col md={3}>
            <Form.Group className="mb-3">
              <Form.Label>PO Type</Form.Label>
              <Form.Select
                name="po_type"
                value={values.po_type}
                onChange={e => handleChange('po_type', e.target.value)}
              >
                <option value="">Select...</option>
                {PO_TYPE.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>

          <Col md={3}>
            <Form.Group className="mb-3">
              <Form.Label>
                Expiry date <span className="text-danger">*</span>
              </Form.Label>
              <DatePicker
                selected={
                  values.expiration_date
                    ? new Date(values.expiration_date)
                    : null
                }
                onChange={handleExpirationDateChange}
                dateFormat="yyyy-MM-dd"
                className={`form-control ${
                  touched.expiration_date && errors.expiration_date
                    ? 'is-invalid'
                    : ''
                }`}
                placeholderText="YYYY-MM-DD"
                isClearable
              />
              {touched.expiration_date && errors.expiration_date && (
                <Form.Control.Feedback type="invalid" className="d-block">
                  {errors.expiration_date}
                </Form.Control.Feedback>
              )}
            </Form.Group>
          </Col>

          <Col md={3}>
            <Form.Group className="mb-3">
              <Form.Label>
                Currency <span className="text-danger">*</span>
              </Form.Label>
              <Form.Select
                name="currency"
                value={values.currency}
                onChange={e => handleChange('currency', e.target.value)}
                onBlur={() => handleBlur('currency')}
                isInvalid={touched.currency && errors.currency}
              >
                <option value="">Select...</option>
                {CURRENCY_LIST.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {errors.currency}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>
      </div>

      {/* Vendor Details Section */}
      {isCreate && (
        <div className="mb-4 border-bottom border-dotted">
          <h5 className="mb-3 fs-9">Vendor Details</h5>

          <Row>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Vendor Name <span className="text-danger">*</span>
                </Form.Label>
                <SearchableInput
                  apiService={vendorNameApi}
                  searchField="name__icontains"
                  value={values.vendorName}
                  onChange={handleVendorInputChange}
                  onSelect={handleVendorSelect}
                  onBlur={() => handleBlur('vendor')}
                  placeholder="Enter Vendor Name"
                  renderItem={item => (
                    <div className="font-medium">{item?.name}</div>
                  )}
                  visibleItems={2}
                  disabled={false}
                  className={
                    touched.vendor && errors.vendor ? 'is-invalid' : ''
                  }
                />
                {touched.vendor && errors.vendor && (
                  <Form.Control.Feedback type="invalid" className="d-block">
                    {errors.vendor}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>
          </Row>
        </div>
      )}

      {/* Logistic Details Section */}
      <div className="mb-4 ">
        <h5 className="mb-3 fs-9">Logistic Details</h5>

        <Row>
          <Col md={3}>
            <Form.Group className="mb-3">
              <Form.Label>Shipping Type</Form.Label>
              <Form.Select
                name="shipping_type"
                value={values.shipping_type}
                onChange={e => handleChange('shipping_type', e.target.value)}
              >
                <option value="">Select...</option>
                {PO_SHIPPING_TYPE.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>

          <Col md={3}>
            <Form.Group className="mb-3">
              <Form.Label>Shipping Charge</Form.Label>
              <Form.Control
                type="text"
                name="shipping_charge"
                value={values.shipping_charge}
                onChange={e => handleChange('shipping_charge', e.target.value)}
                placeholder="Shipping Charge"
              />
            </Form.Group>
          </Col>

          <Col md={3}>
            <Form.Group className="mb-3">
              <Form.Label>
                Shipping Charge HSN Code <span className="text-danger">*</span>
              </Form.Label>

              <Form.Control
                type="text"
                name="shipping_charge_hsn_code"
                value={values.shipping_charge_hsn_code}
                onChange={e =>
                  handleChange('shipping_charge_hsn_code', e.target.value)
                }
                onBlur={() => handleBlur('shipping_charge_hsn_code')}
                isInvalid={
                  touched.shipping_charge_hsn_code &&
                  errors.shipping_charge_hsn_code
                }
                placeholder="Shipping Charge HSN Code"
              />
              <Form.Control.Feedback type="invalid">
                {errors.shipping_charge_hsn_code}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          <Col md={3}>
            <Form.Group className="mb-3">
              <Form.Label>Shipping Comments</Form.Label>
              <Form.Control
                type="text"
                name="shipping_comments"
                value={values.shipping_comments}
                onChange={e =>
                  handleChange('shipping_comments', e.target.value)
                }
                placeholder="Shipping Comments"
              />
            </Form.Group>
          </Col>
        </Row>
      </div>

      <div className="d-flex gap-2 mt-4">
        <Button
          type="submit"
          variant="primary"
          disabled={!isValid || isLoading}
        >
          {isLoading ? 'Saving...' : 'Save'}
        </Button>

        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </Form>
  );
};

export default PurchaseOrderForm;
